import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Map, ZoomControl, Overlay } from 'pigeon-maps';
import { useSelector } from 'react-redux';
import { selectAirportLocations } from '_redux/slices/timeline/timeline-areas';
import { selectFirebaseInfo } from '_redux/slices/main-screen/user';
import { SelectCurrentCoordinates } from '_redux/slices/timeline/timeline-live-location-tab';
import { getToken } from '_services/user-service';
import { buildCentroids } from './asset-tracking-utils';

const OSM_PROVIDER       = (x, y, z) => `https://tile.openstreetmap.org/${z}/${x}/${y}.png`;
const SATELLITE_PROVIDER = (x, y, z) => `https://services.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/${z}/${y}/${x}`;

// Blue intensity palette — dense=deep blue, sparse=pale blue, same two-pass technique as movement heatmap
let _palette = null;
function getBluePalette() {
    if (_palette) return _palette;
    const c = document.createElement('canvas');
    c.width = 256; c.height = 1;
    const ctx = c.getContext('2d');
    const g = ctx.createLinearGradient(0, 0, 256, 0);
    g.addColorStop(0,    'rgba(59,130,246,0)');
    g.addColorStop(0.15, 'rgb(31, 110, 237)');
    g.addColorStop(0.40, 'rgb(68, 150, 250)');
    g.addColorStop(0.65, 'rgb(124, 182, 249)');
    g.addColorStop(0.85, 'rgb(163, 207, 252)');
    g.addColorStop(1,    'rgba(219,234,254,1)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 256, 1);
    const d = ctx.getImageData(0, 0, 256, 1).data;
    _palette = new Uint8ClampedArray(256 * 3);
    for (let i = 0; i < 256; i++) {
        _palette[i * 3]     = d[i * 4];
        _palette[i * 3 + 1] = d[i * 4 + 1];
        _palette[i * 3 + 2] = d[i * 4 + 2];
    }
    return _palette;
}

function renderHeatmap(canvas, points, latLngToPixel, zoom) {
    const ctx = canvas.getContext('2d');
    const w = canvas.width;
    const h = canvas.height;
    ctx.clearRect(0, 0, w, h);
    if (!points.length) return;

    // Tighter radius — the old formula was too aggressive at high zoom
    const radius = Math.max(8, Math.min(38, Math.pow(2, zoom - 16) * 12));
    const maxSamples = Math.max(...points.map(p => p.sampleCount || 1));

    // Pass 1: greyscale intensity blobs
    for (const pt of points) {
        const [x, y] = latLngToPixel([pt.lat, pt.lng]);
        if (x < -radius || y < -radius || x > w + radius || y > h + radius) continue;
        const weight = 0.07 + ((pt.sampleCount || 1) / maxSamples) * 0.50;
        const g = ctx.createRadialGradient(x, y, 0, x, y, radius);
        g.addColorStop(0,   `rgba(0,0,0,${weight.toFixed(3)})`);
        g.addColorStop(0.4, `rgba(0,0,0,${(weight * 0.4).toFixed(3)})`);
        g.addColorStop(1,   'rgba(0,0,0,0)');
        ctx.fillStyle = g;
        ctx.beginPath();
        ctx.arc(x, y, radius, 0, Math.PI * 2);
        ctx.fill();
    }

    // Pass 2: colourise via blue palette, cap opacity so map tiles show through
    const palette = getBluePalette();
    const imageData = ctx.getImageData(0, 0, w, h);
    const pix = imageData.data;
    for (let i = 0; i < pix.length; i += 4) {
        const a = pix[i + 3];
        if (a === 0) continue;
        const idx = Math.min(255, Math.floor(a * 2.8));
        pix[i]     = palette[idx * 3];
        pix[i + 1] = palette[idx * 3 + 1];
        pix[i + 2] = palette[idx * 3 + 2];
        pix[i + 3] = Math.min(145, Math.floor(a * 3.0)); // ~57% max opacity
    }
    ctx.putImageData(imageData, 0, 0);
}

// Direct pigeon-maps child — pigeon injects latLngToPixel + mapState
function InfraHeatmapCanvas({ points, projectedRef, latLngToPixel, mapState }) {
    const canvasRef = useRef(null);
    const rafRef    = useRef(null);

    useEffect(() => {
        if (!canvasRef.current || !latLngToPixel || !mapState?.width) return;

        // Defer heavy pixel work so map pan/zoom events aren't blocked
        if (rafRef.current) cancelAnimationFrame(rafRef.current);
        rafRef.current = requestAnimationFrame(() => {
            if (!canvasRef.current) return;
            const canvas = canvasRef.current;
            canvas.width  = mapState.width;
            canvas.height = mapState.height;

            projectedRef.current = points.map(pt => {
                const [x, y] = latLngToPixel([pt.lat, pt.lng]);
                return { x, y, point: pt };
            });

            renderHeatmap(canvas, points, latLngToPixel, mapState.zoom ?? 17);
            rafRef.current = null;
        });

        return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
    }, [points, latLngToPixel, mapState]);

    return (
        <canvas
            ref={canvasRef}
            style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 2 }}
        />
    );
}

// Floating toggle button inside map
function LayerToggle({ label, active, activeColor, onClick }) {
    return (
        <button
            onClick={onClick}
            style={{
                display: 'flex', alignItems: 'center', gap: 8,
                padding: '9px 16px 9px 12px',
                borderRadius: 12, border: 'none', cursor: 'pointer',
                background: active ? activeColor : 'rgba(255,255,255,0.92)',
                color: active ? '#fff' : '#334155',
                backdropFilter: 'blur(12px)',
                WebkitBackdropFilter: 'blur(12px)',
                boxShadow: active
                    ? `0 4px 18px ${activeColor}55, 0 1px 4px rgba(0,0,0,0.1)`
                    : '0 2px 10px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.8)',
                fontWeight: 600, fontSize: '0.8rem',
                transition: 'all 0.18s ease',
                whiteSpace: 'nowrap', userSelect: 'none',
            }}
        >
            <span style={{
                width: 9, height: 9, borderRadius: '50%', flexShrink: 0,
                background: active ? 'rgba(255,255,255,0.95)' : activeColor,
                boxShadow: active ? '0 0 0 2px rgba(255,255,255,0.3)' : `0 0 8px ${activeColor}aa`,
                transition: 'all 0.18s ease',
            }} />
            {label}
        </button>
    );
}

function sortFloors(floors) {
    const order = (f) => {
        if (/ground/i.test(f)) return 0;
        if (/basement/i.test(f)) return -1;
        const m = f.match(/(\d+)/);
        return m ? parseInt(m[1]) : 999;
    };
    return [...floors].sort((a, b) => order(a) - order(b));
}

export default function AssetTrackingInfrastructure() {
    const dbUser        = useSelector(selectFirebaseInfo);
    const locations     = useSelector(selectAirportLocations);
    const airportCoords = useSelector(SelectCurrentCoordinates);
    const iata = dbUser?.details?.authority;

    const [floors, setFloors]               = useState([]);
    const [selectedFloor, setSelectedFloor] = useState(null);
    const [points, setPoints]               = useState([]);
    const [loading, setLoading]             = useState(false);
    const [showWifi, setShowWifi]           = useState(true);
    const [showBeacons, setShowBeacons]     = useState(false);
    const [satellite, setSatellite]         = useState(false);
    const [mapCenter, setMapCenter]         = useState([53.4264, -6.2499]);
    const [zoom, setZoom]                   = useState(17);
    const [tooltipContent, setTooltipContent] = useState(null); // { label, count }

    const containerRef   = useRef(null);
    const projectedRef   = useRef([]);
    const tooltipRef     = useRef(null);
    const lineRef        = useRef(null);
    const dotRef         = useRef(null);
    const svgRef         = useRef(null);
    const showWifiRef    = useRef(showWifi);
    const beaconHoverRef = useRef(false);

    const locationTitleMap = useMemo(() => {
        const m = {};
        for (const loc of (locations || [])) m[loc._id] = loc.title;
        return m;
    }, [locations]);

    const centroids = useMemo(() => buildCentroids(locations), [locations]);

    useEffect(() => {
        showWifiRef.current = showWifi;
        if (!showWifi) {
            if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
            if (svgRef.current)     svgRef.current.style.opacity     = '0';
            setTooltipContent(null);
        }
    }, [showWifi]);

    useEffect(() => {
        if (airportCoords?.lat && airportCoords?.lon) {
            setMapCenter([parseFloat(airportCoords.lat), parseFloat(airportCoords.lon)]);
        }
    }, [airportCoords]);

    useEffect(() => {
        if (!iata) return;
        (async () => {
            try {
                const token = await getToken();
                const res = await fetch(`/api/v1/mobileusers/wifi-survey/floors/${iata}`, {
                    headers: { 'Firebase-UID': token },
                });
                const data = await res.json();
                const sorted = sortFloors(data.floors || []);
                setFloors(sorted);
                if (sorted.length) setSelectedFloor(sorted[0]);
            } catch (e) {
                console.error('Failed to fetch floors:', e);
            }
        })();
    }, [iata]);

    useEffect(() => {
        if (!iata || !selectedFloor) return;
        setLoading(true);
        setPoints([]);
        (async () => {
            try {
                const token = await getToken();
                const res = await fetch(
                    `/api/v1/mobileusers/wifi-survey/coverage/${iata}/${encodeURIComponent(selectedFloor)}`,
                    { headers: { 'Firebase-UID': token } }
                );
                const data = await res.json();
                setPoints(data.points || []);
            } catch (e) {
                console.error('Failed to fetch coverage:', e);
            } finally {
                setLoading(false);
            }
        })();
    }, [iata, selectedFloor]);

    // Hover + connector line — all DOM direct, zero re-renders per frame
    const handleMouseMove = useCallback((e) => {
        if (beaconHoverRef.current) return;
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;

        let nearest = null, nearestDist = Infinity, nearestX = 0, nearestY = 0;
        if (showWifiRef.current) {
            for (const { x, y, point } of projectedRef.current) {
                const d = Math.hypot(x - cx, y - cy);
                if (d < nearestDist) { nearestDist = d; nearest = point; nearestX = x; nearestY = y; }
            }
        }

        const isNear = nearest && nearestDist < 55;

        // Move tooltip via DOM (position: fixed using clientX/clientY)
        if (tooltipRef.current) {
            if (isNear) {
                tooltipRef.current.style.left    = `${e.clientX + 18}px`;
                tooltipRef.current.style.top     = `${e.clientY - 36}px`;
                tooltipRef.current.style.opacity = '1';
            } else {
                tooltipRef.current.style.opacity = '0';
            }
        }

        // SVG connector: cursor → matched point centre
        if (svgRef.current && lineRef.current && dotRef.current) {
            if (isNear) {
                lineRef.current.setAttribute('x1', cx);
                lineRef.current.setAttribute('y1', cy);
                lineRef.current.setAttribute('x2', nearestX);
                lineRef.current.setAttribute('y2', nearestY);
                dotRef.current.setAttribute('cx', nearestX);
                dotRef.current.setAttribute('cy', nearestY);
                svgRef.current.style.opacity = '1';
            } else {
                svgRef.current.style.opacity = '0';
            }
        }

        // Only setState when the hovered point actually changes (not every frame)
        if (isNear) {
            const label = nearest.freeTextLabels?.[0]
                ?? (nearest.locationIds?.[0] ? (locationTitleMap[nearest.locationIds[0]] ?? nearest.locationIds[0]) : null)
                ?? 'Unknown area';
            const ssids = nearest.ssids || [];
            setTooltipContent(prev =>
                prev?.label === label && prev?.count === nearest.sampleCount ? prev
                : { label, count: nearest.sampleCount, ssids }
            );
        } else {
            setTooltipContent(null);
        }
    }, [locationTitleMap]);

    const handleMouseLeave = useCallback(() => {
        if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
        if (svgRef.current)     svgRef.current.style.opacity     = '0';
        setTooltipContent(null);
    }, []);

    const handleBeaconEnter = useCallback((e, title) => {
        beaconHoverRef.current = true;
        if (svgRef.current)     svgRef.current.style.opacity     = '0';
        if (tooltipRef.current) {
            tooltipRef.current.style.left    = `${e.clientX + 18}px`;
            tooltipRef.current.style.top     = `${e.clientY - 36}px`;
            tooltipRef.current.style.opacity = '1';
        }
        setTooltipContent({ label: title, count: null });
    }, []);

    const handleBeaconMove = useCallback((e) => {
        if (tooltipRef.current) {
            tooltipRef.current.style.left = `${e.clientX + 18}px`;
            tooltipRef.current.style.top  = `${e.clientY - 36}px`;
        }
    }, []);

    const handleBeaconLeave = useCallback(() => {
        beaconHoverRef.current = false;
        if (tooltipRef.current) tooltipRef.current.style.opacity = '0';
        setTooltipContent(null);
    }, []);

    const beaconMarkers = useMemo(() => {
        if (!showBeacons) return null;
        return Object.entries(centroids).map(([title, { lat, lon }]) => (
            <Overlay key={title} anchor={[lat, lon]} offset={[7, 7]}>
                <div
                    onMouseEnter={(e) => handleBeaconEnter(e, title)}
                    onMouseMove={handleBeaconMove}
                    onMouseLeave={handleBeaconLeave}
                    style={{
                        width: 13, height: 13, borderRadius: '50%',
                        background: '#f59e0b', border: '2px solid #fff',
                        boxShadow: '0 1px 6px rgba(0,0,0,0.45)',
                        cursor: 'default',
                    }}
                />
            </Overlay>
        ));
    }, [showBeacons, centroids, handleBeaconEnter, handleBeaconMove, handleBeaconLeave]);

    return (
        <div style={{ height: '100%', width: '100%', position: 'relative' }}>

            {/* Map — full height, no header bar */}
            <div
                ref={containerRef}
                style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}
                onMouseMove={handleMouseMove}
                onMouseLeave={handleMouseLeave}
            >
                <Map
                    center={mapCenter}
                    zoom={zoom}
                    provider={satellite ? SATELLITE_PROVIDER : OSM_PROVIDER}
                    onBoundsChanged={({ center, zoom: z }) => { setMapCenter(center); setZoom(z); }}
                    style={{ width: '100%', height: '100%' }}
                >
                    <ZoomControl />
                    {showWifi && (
                        <InfraHeatmapCanvas
                            points={points}
                            projectedRef={projectedRef}
                        />
                    )}
                    {beaconMarkers}
                </Map>

                {/* SVG connector — cursor to matched point */}
                <svg
                    ref={svgRef}
                    style={{
                        position: 'absolute', inset: 0, width: '100%', height: '100%',
                        pointerEvents: 'none', zIndex: 600, opacity: 0,
                        transition: 'opacity 0.1s',
                    }}
                >
                    <line
                        ref={lineRef}
                        stroke="rgba(59,130,246,0.65)"
                        strokeWidth="1.5"
                        strokeDasharray="4 3"
                    />
                    {/* Dot at exact matched reference centroid */}
                    <circle ref={dotRef} r="4" fill="#3b82f6" stroke="#fff" strokeWidth="1.5" />
                </svg>

                {/* ── Layer toggles + floor selector — top-right ── */}
                <div style={{
                    position: 'absolute', top: 14, right: 14, zIndex: 700,
                    display: 'flex', flexDirection: 'column', gap: 8,
                    alignItems: 'stretch',
                }}>
                    <LayerToggle
                        label="Satellite"
                        active={satellite}
                        activeColor="#1e293b"
                        onClick={() => setSatellite(v => !v)}
                    />
                    <LayerToggle
                        label="WiFi Coverage"
                        active={showWifi}
                        activeColor="#3b82f6"
                        onClick={() => setShowWifi(v => !v)}
                    />
                    <LayerToggle
                        label="BLE Beacons"
                        active={showBeacons}
                        activeColor="#f59e0b"
                        onClick={() => setShowBeacons(v => !v)}
                    />

                    {/* Floor selector — only when WiFi is on and floors exist */}
                    {showWifi && floors.length > 0 && (
                        <div style={{
                            background: 'rgba(255,255,255,0.95)',
                            backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)',
                            borderRadius: 12, overflow: 'hidden',
                            boxShadow: '0 2px 14px rgba(0,0,0,0.14)',
                        }}>
                            <div style={{
                                padding: '8px 14px 5px',
                                fontSize: '0.61rem', fontWeight: 800,
                                letterSpacing: '0.1em', textTransform: 'uppercase',
                                color: '#3b82f6',
                            }}>
                                WiFi Floor
                            </div>
                            {floors.map(f => (
                                <button key={f} onClick={() => setSelectedFloor(f)} style={{
                                    display: 'block', width: '100%', textAlign: 'left',
                                    padding: '7px 14px', border: 'none', cursor: 'pointer',
                                    fontSize: '0.78rem', fontWeight: selectedFloor === f ? 700 : 400,
                                    background: selectedFloor === f ? '#3b82f6' : 'transparent',
                                    color: selectedFloor === f ? '#fff' : '#374151',
                                    transition: 'background 0.12s',
                                }}>{f}</button>
                            ))}
                            {loading && (
                                <div style={{ padding: '6px 14px 8px', fontSize: '0.68rem', color: '#94a3b8' }}>
                                    Loading…
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* ── Legend — bottom-left ── */}
                <div style={{
                    position: 'absolute', bottom: 14, left: 14, zIndex: 600,
                    background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)',
                    borderRadius: 10, padding: '8px 12px',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
                    pointerEvents: 'none', display: 'flex', flexDirection: 'column', gap: 6,
                }}>
                    {showWifi && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{
                                width: 38, height: 8, borderRadius: 4,
                                background: 'linear-gradient(to right, rgba(219,234,254,0.6), rgba(59,130,246,0.85))',
                            }} />
                            <span style={{ fontSize: '0.67rem', color: '#475569' }}>Sparse → Dense</span>
                        </div>
                    )}
                    {showBeacons && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                            <div style={{
                                width: 11, height: 11, borderRadius: '50%', flexShrink: 0,
                                background: '#f59e0b', border: '2px solid #fff',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.25)',
                            }} />
                            <span style={{ fontSize: '0.67rem', color: '#475569' }}>BLE Beacon</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Tooltip — position: fixed, follows cursor via DOM ref, never re-renders position */}
            <div
                ref={tooltipRef}
                style={{
                    position: 'fixed', zIndex: 9999,
                    background: '#1e293b', color: '#f8fafc',
                    borderRadius: 9, padding: '8px 13px',
                    pointerEvents: 'none', opacity: 0,
                    transition: 'opacity 0.12s',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    border: '1px solid rgba(59,130,246,0.3)',
                    minWidth: 140, maxWidth: 240,
                }}
            >
                {tooltipContent && (
                    <>
                        <div style={{ fontWeight: 700, fontSize: '0.8rem', marginBottom: 3 }}>
                            {tooltipContent.label}
                        </div>
                        {tooltipContent.count != null && (
                            <div style={{ color: '#94a3b8', fontSize: '0.69rem' }}>
                                {tooltipContent.count} sample{tooltipContent.count !== 1 ? 's' : ''}
                            </div>
                        )}
                        {tooltipContent.ssids?.length > 0 && (
                            <div style={{ marginTop: 5, paddingTop: 5, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                                {tooltipContent.ssids.slice(0, 4).map(s => (
                                    <div key={s} style={{ fontSize: '0.66rem', color: '#7dd3fc', lineHeight: 1.4, fontFamily: 'monospace' }}>{s}</div>
                                ))}
                                {tooltipContent.ssids.length > 4 && (
                                    <div style={{ fontSize: '0.63rem', color: '#64748b', marginTop: 1 }}>+{tooltipContent.ssids.length - 4} more</div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
